import { useMutation } from 'react-query';

import fileV2 from '@/services/api/fileV2';

declare global {
  interface ImageFile {
    /**
     * 고유키
     */
    id: number;
    /**
     * 파일 이름
     */
    name?: string;
    /**
     * 원본 파일 URL (API 통일 필요)
     */
    path: string;
    /**
     * 리사이징된 파일 URL
     */
    thumbnailPath: string;
    /**
     * 파일 크기
     */
    size?: number;
    /**
     * 파일 확장자
     */
    type?: string;
    /**
     * 파일 확장자
     */
    extension?: string;
  }
}

export const filesToFormData = (files: (Blob | undefined)[]) => {
  // File type extends Blob type
  const formData = new FormData();

  for (let i = 0; i < files.length; i++) {
    if (files[i]) {
      formData.append('files', files[i] as Blob);
    }
  }

  return formData;
};

export const fileService = {
  async uploadFiles(files: FormData) {
    const { data } = await fileV2.post<ImageFile[]>('/files/upload', files, {
      headers: { 'content-type': 'multipart/form-data' },
    });
    return data;
  },
  async deleteFiles(fileIds: number[]) {
    const { data } = await fileV2.delete<number[]>('/files', {
      data: { files: fileIds },
    }); // delete 메서드에서는 config.data로 바디 전송
    return data;
  },
};

export const useAsyncUploadFiles = (
  onSuccess?: (files: ImageFile[]) => void,
  onError?: (error: V2ErrorType) => void
) => {
  const { mutate, isLoading } = useMutation<ImageFile[], V2ErrorType, FormData>(
    (files) => fileService.uploadFiles(files),
    {
      onSuccess,
      onError,
    }
  );

  if (isLoading) throw new Promise((resolve) => resolve(null));

  return mutate;
};

export const useAsyncDeleteFiles = (
  onSuccess?: (data: number[]) => void,
  onError?: (error: V2ErrorType) => void
) => {
  const { mutate, isLoading } = useMutation<number[], V2ErrorType, number[]>(
    (fileIds) => fileService.deleteFiles(fileIds),
    {
      onSuccess,
      onError,
    }
  );

  if (isLoading) throw new Promise((resolve) => resolve(null));

  return mutate;
};

/**
 * TODO: 파일 업로드 방식을 SUSPENSE 모드와 아닌 모드로 나누어 변경하기
 */
export const useUploadFiles = (
  onSuccess?: (data: ImageFile[]) => void,
  onError?: (error: V2ErrorType) => void
) => {
  return useMutation<ImageFile[], V2ErrorType, FormData>(
    (files) => fileService.uploadFiles(files),
    {
      onSuccess,
      onError,
    }
  );
};

/**
 * TODO: 파일 업로드 방식을 SUSPENSE 모드와 아닌 모드로 나누어 변경하기
 */
export const useDeleteFiles = (
  onSuccess?: (data: number[]) => void,
  onError?: (error: V2ErrorType) => void
) => {
  return useMutation<number[], V2ErrorType, number[]>(
    (fileIds) => fileService.deleteFiles(fileIds),
    {
      onSuccess,
      onError,
    }
  );
};
